import dayjs from 'dayjs';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { DaysView } from './DaysView';
import { MonthView } from './MonthView';
import { YearView } from './YearView';
import { t } from '../../services/translation/translation';
import { PrimaryButton } from '../button/PrimaryButton';
import { SecondaryButton } from '../button/SecondaryButton';

type Props = {
  onChange: (_timestamp: number | undefined) => void;
  initialDate?: number;
  inline?: boolean;
  hideActions?: boolean;
};

export const DatePicker = ({ onChange, initialDate, inline, hideActions }: Props) => {
  const [ui, setUi] = useState<'day' | 'month' | 'year'>('day');
  const [date, setDate] = useState<number | undefined>(initialDate && dayjs(initialDate).startOf('day').valueOf());
  const [year, setYear] = useState<number>(dayjs(initialDate).get('year'));
  const [month, setMonth] = useState<number>(dayjs(initialDate).get('month'));

  const handleReset = () => {
    setYear(dayjs().get('year'));
    setMonth(dayjs().get('month'));
    setDate(undefined);
    onChange(undefined);
    setUi('day');
  };

  const handleSelectDay = (timestamp: number) => {
    setYear(dayjs(timestamp).get('year'));
    setMonth(dayjs(timestamp).get('month'));
    setUi('day');
    setDate(timestamp);
    onChange(timestamp);
  };

  const handleSelectMonth = (selectedMonth: number) => {
    setMonth(selectedMonth);
    setUi('day');
  };

  const handleSelectYear = (selectedYear: number) => {
    setYear(selectedYear);
    setUi('month');
  };

  return (
    <div className={twMerge('inline-block rounded-lg bg-white dark:bg-gray-700 p-4', !inline && 'shadow-lg')}>
      {ui === 'day' ? (
        <DaysView
          year={year}
          month={month}
          onSelectDay={handleSelectDay}
          selectedTimestamp={date}
          setYear={setYear}
          setMonth={setMonth}
          onHeaderClick={() => setUi('month')}
        />
      ) : ui === 'month' ? (
        <MonthView
          year={year}
          month={month}
          onSelectMonth={handleSelectMonth}
          setYear={setYear}
          onHeaderClick={() => setUi('year')}
        />
      ) : (
        <YearView year={year} onSelectYear={handleSelectYear} />
      )}

      {!hideActions ? (
        <div className="flex space-x-2 mt-2">
          <PrimaryButton className="w-full" onClick={() => handleSelectDay(dayjs().startOf('day').valueOf())}>
            {t('common.today')}
          </PrimaryButton>

          <SecondaryButton className="w-full" onClick={handleReset}>
            {t('common.reset')}
          </SecondaryButton>
        </div>
      ) : null}
    </div>
  );
};
